/**
 * Overfitter Enemy (Redesigned)
 * Anchored to the left deletion wall - a massive maw that "eats" the level
 * The embodiment of the deletion zone, always pursuing
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';

export class Overfitter {
    constructor(scene) {
        this.scene = scene;

        // Core properties
        this.x = 0;
        this.y = this.scene.scale.height / 2;
        this.width = 120;

        // Visual intensity (increases as player gets closer)
        this.intensity = 0;
        this.mouthOpen = 0;
        this.eyeTrackX = 0;
        this.eyeTrackY = 0;

        // Animation timers
        this.time = 0;
        this.breatheTime = 0;

        // Create sprite using custom image (1080px scaled to ~150px wide)
        this.sprite = scene.add.image(0, this.scene.scale.height / 2, 'overfitter');
        this.sprite.setDepth(100);  // High depth to ensure visibility
        this.baseScale = 150 / 1080;  // Scale to ~150px
        this.sprite.setScale(this.baseScale);
        this.sprite.setOrigin(0.5, 0.5);

        // Aggression level
        this.aggression = 0;
    }

    update(delta, cameraX, playerX, playerY) {
        this.time += delta;
        this.breatheTime += delta * 0.003;

        // Anchor to left edge of camera (positioned so it's clearly visible)
        this.x = cameraX + 150;

        // Calculate player proximity (0 = far, 1 = very close)
        const distanceFromWall = playerX - cameraX;
        const maxSafeDistance = 350;
        this.intensity = 1 - Math.min(distanceFromWall / maxSafeDistance, 1);
        this.intensity = Math.max(0, this.intensity);

        // Aggression ramps up faster than intensity
        this.aggression = Math.pow(this.intensity, 0.7);

        // Update sprite position
        this.sprite.setPosition(this.x, this.scene.scale.height / 2);

        // Pulsing/breathing animation (relative to base scale)
        const breathe = 1 + Math.sin(this.breatheTime) * 0.1 * (1 + this.aggression);
        const shake = this.aggression > 0.7 ? (Math.random() - 0.5) * this.aggression * 5 : 0;

        this.sprite.setScale(this.baseScale * (breathe + this.intensity * 0.2));
        this.sprite.x += shake;

        // Tint gets redder with aggression
        if (this.intensity > 0.5) {
            const red = Math.floor(255);
            const other = Math.floor(255 - this.intensity * 100);
            this.sprite.setTint(Phaser.Display.Color.GetColor(red, other, other));
        } else {
            this.sprite.clearTint();
        }

        return this.intensity;
    }

    draw() {
        this.bodyGraphics.clear();
        this.faceGraphics.clear();
        this.teethGraphics.clear();

        const breathe = Math.sin(this.breatheTime) * 10 * (1 + this.aggression);
        const shake = this.aggression > 0.7 ? (Math.random() - 0.5) * this.aggression * 10 : 0;

        // === BODY: Amorphous dark mass ===
        this.drawBody(shake, breathe);

        // === FACE: The watching presence ===
        this.drawFace(shake, breathe);

        // === MOUTH: The consuming maw ===
        this.drawMouth(shake, breathe);
    }

    drawBody(shake, breathe) {
        const centerY = this.scene.scale.height / 2;

        // Multiple layers of darkness emanating from the wall
        for (let layer = 0; layer < 5; layer++) {
            const layerWidth = this.width + layer * 30 + breathe;
            const alpha = (0.3 - layer * 0.05) * (0.5 + this.intensity * 0.5);

            this.bodyGraphics.fillStyle(0x0a0005, alpha);

            // Organic, pulsing shape
            this.bodyGraphics.beginPath();
            this.bodyGraphics.moveTo(this.x - 50, 0);

            // Wavy edge
            const segments = 20;
            for (let i = 0; i <= segments; i++) {
                const t = i / segments;
                const y = t * this.scene.scale.height;
                const wave = Math.sin(y * 0.02 + this.breatheTime + layer) * 20;
                const pulse = Math.sin(this.breatheTime * 2 + t * Math.PI) * 10;
                const x = this.x + layerWidth + wave + pulse + shake;
                this.bodyGraphics.lineTo(x, y);
            }

            this.bodyGraphics.lineTo(this.x - 50, this.scene.scale.height);
            this.bodyGraphics.closePath();
            this.bodyGraphics.fillPath();
        }

        // Core darkness
        this.bodyGraphics.fillStyle(0x000000, 1);
        this.bodyGraphics.fillRect(this.x - 100, 0, 100, this.scene.scale.height);

        // Pulsing veins
        if (this.intensity > 0.3) {
            this.drawVeins(shake, breathe);
        }
    }

    drawVeins(shake, breathe) {
        const veinCount = Math.floor(5 + this.intensity * 5);

        for (let i = 0; i < veinCount; i++) {
            const startY = (i / veinCount) * this.scene.scale.height;
            const pulse = Math.sin(this.breatheTime * 3 + i) * 0.5 + 0.5;

            this.bodyGraphics.lineStyle(
                1 + this.intensity * 2,
                0x330011,
                0.3 + pulse * 0.3 * this.intensity
            );

            this.bodyGraphics.beginPath();
            this.bodyGraphics.moveTo(this.x, startY);

            let x = this.x;
            let y = startY;

            for (let j = 0; j < 5; j++) {
                x += 20 + Math.random() * 20;
                y += (Math.random() - 0.5) * 40;
                this.bodyGraphics.lineTo(x + shake, y);
            }

            this.bodyGraphics.strokePath();
        }
    }

    drawFace(shake, breathe) {
        const centerY = this.scene.scale.height / 2;
        const faceX = this.x + 50 + breathe * 0.5;

        // Eye positions
        const eyeSpacing = 60 + this.intensity * 20;
        const leftEyeY = centerY - eyeSpacing / 2;
        const rightEyeY = centerY + eyeSpacing / 2;
        const eyeX = faceX + shake;

        // Draw eyes
        this.drawEye(eyeX, leftEyeY + shake * 0.5, 1);
        this.drawEye(eyeX, rightEyeY - shake * 0.5, -1);

        // Additional eyes appear at high intensity
        if (this.intensity > 0.6) {
            const extraAlpha = (this.intensity - 0.6) * 2.5;
            this.drawEye(eyeX - 20, leftEyeY - 50, 1, extraAlpha * 0.5, 0.6);
            this.drawEye(eyeX - 20, rightEyeY + 50, -1, extraAlpha * 0.5, 0.6);
        }

        if (this.intensity > 0.8) {
            const extraAlpha = (this.intensity - 0.8) * 5;
            this.drawEye(eyeX - 30, centerY, 0, extraAlpha * 0.3, 0.4);
        }
    }

    drawEye(x, y, verticalOffset, alphaMultiplier = 1, sizeMultiplier = 1) {
        const baseSize = (15 + this.intensity * 10) * sizeMultiplier;
        const irisSize = baseSize * 0.6;
        const pupilSize = irisSize * (0.4 + this.intensity * 0.3);

        // Calculate look direction
        const lookAngle = Math.atan2(
            this.eyeTrackY - y,
            this.eyeTrackX - x
        );
        const lookDistance = Math.min(baseSize * 0.3, 8);
        const irisOffsetX = Math.cos(lookAngle) * lookDistance;
        const irisOffsetY = Math.sin(lookAngle) * lookDistance;

        // Outer glow
        const glowPulse = Math.sin(this.breatheTime * 2) * 0.2 + 0.8;
        this.faceGraphics.fillStyle(0x440000, 0.3 * glowPulse * alphaMultiplier);
        this.faceGraphics.fillCircle(x, y, baseSize * 1.5);

        // Sclera (bloodshot at high intensity)
        const bloodshot = this.intensity > 0.5 ? 0x331111 : 0x221111;
        this.faceGraphics.fillStyle(bloodshot, 0.9 * alphaMultiplier);
        this.faceGraphics.fillCircle(x, y, baseSize);

        // Blood vessels in eye
        if (this.intensity > 0.4) {
            this.faceGraphics.lineStyle(1, 0x660000, 0.5 * this.intensity * alphaMultiplier);
            for (let i = 0; i < 5; i++) {
                const angle = (i / 5) * Math.PI * 2;
                this.faceGraphics.beginPath();
                this.faceGraphics.moveTo(x, y);
                this.faceGraphics.lineTo(
                    x + Math.cos(angle) * baseSize * 0.9,
                    y + Math.sin(angle) * baseSize * 0.9
                );
                this.faceGraphics.strokePath();
            }
        }

        // Iris - sickly color
        const irisColor = Phaser.Display.Color.GetColor(
            100 + Math.floor(this.intensity * 50),
            20,
            20
        );
        this.faceGraphics.fillStyle(irisColor, 0.95 * alphaMultiplier);
        this.faceGraphics.fillCircle(x + irisOffsetX, y + irisOffsetY, irisSize);

        // Pupil - dilates with aggression
        this.faceGraphics.fillStyle(0x000000, 1 * alphaMultiplier);
        this.faceGraphics.fillCircle(x + irisOffsetX, y + irisOffsetY, pupilSize);

        // Highlight
        this.faceGraphics.fillStyle(0xFFFFFF, 0.3 * alphaMultiplier);
        this.faceGraphics.fillCircle(
            x + irisOffsetX - pupilSize * 0.3,
            y + irisOffsetY - pupilSize * 0.3,
            pupilSize * 0.3
        );
    }

    drawMouth(shake, breathe) {
        const centerY = this.scene.scale.height / 2;
        const mouthX = this.x + 80 + breathe;
        const mouthWidth = 40 + this.mouthOpen * 60;
        const mouthHeight = 80 + this.mouthOpen * 100;

        // Mouth opening (vertical maw)
        this.teethGraphics.fillStyle(0x000000, 1);
        this.teethGraphics.fillEllipse(
            mouthX + shake,
            centerY,
            mouthWidth,
            mouthHeight
        );

        // Inner throat glow
        const throatPulse = Math.sin(this.breatheTime * 4) * 0.3 + 0.7;
        this.teethGraphics.fillStyle(0x220000, throatPulse * this.intensity);
        this.teethGraphics.fillEllipse(
            mouthX - 10 + shake,
            centerY,
            mouthWidth * 0.6,
            mouthHeight * 0.7
        );

        // Teeth
        if (this.mouthOpen > 0.3) {
            this.drawTeeth(mouthX + shake, centerY, mouthWidth, mouthHeight);
        }

        // Drool/corruption dripping
        if (this.intensity > 0.5) {
            this.drawDrool(mouthX + shake, centerY, mouthHeight);
        }
    }

    drawTeeth(mouthX, mouthY, mouthWidth, mouthHeight) {
        const teethCount = 8;
        const teethSize = 10 + this.mouthOpen * 15;

        this.teethGraphics.fillStyle(0xDDCCBB, 0.9);

        // Top teeth
        for (let i = 0; i < teethCount; i++) {
            const t = (i / (teethCount - 1)) - 0.5;
            const angle = t * Math.PI * 0.8;
            const x = mouthX + Math.cos(angle + Math.PI / 2) * mouthWidth * 0.8;
            const y = mouthY + Math.sin(angle + Math.PI / 2) * mouthHeight * 0.9;

            // Triangular teeth pointing inward
            this.teethGraphics.beginPath();
            this.teethGraphics.moveTo(x - teethSize * 0.3, y);
            this.teethGraphics.lineTo(x + teethSize * 0.3, y);
            this.teethGraphics.lineTo(x, y + Math.sign(t || 1) * teethSize);
            this.teethGraphics.closePath();
            this.teethGraphics.fillPath();
        }

        // Bottom teeth
        for (let i = 0; i < teethCount; i++) {
            const t = (i / (teethCount - 1)) - 0.5;
            const angle = t * Math.PI * 0.8;
            const x = mouthX + Math.cos(angle - Math.PI / 2) * mouthWidth * 0.8;
            const y = mouthY + Math.sin(angle - Math.PI / 2) * mouthHeight * 0.9;

            this.teethGraphics.beginPath();
            this.teethGraphics.moveTo(x - teethSize * 0.3, y);
            this.teethGraphics.lineTo(x + teethSize * 0.3, y);
            this.teethGraphics.lineTo(x, y - Math.sign(t || 1) * teethSize);
            this.teethGraphics.closePath();
            this.teethGraphics.fillPath();
        }
    }

    drawDrool(mouthX, mouthY, mouthHeight) {
        // Corruption dripping from mouth
        const droolCount = 3 + Math.floor(this.intensity * 4);

        for (let i = 0; i < droolCount; i++) {
            const baseX = mouthX + (Math.random() - 0.5) * 40;
            const baseY = mouthY + mouthHeight * 0.4;
            const length = 20 + Math.random() * 40 * this.intensity;
            const drip = Math.sin(this.breatheTime * 2 + i) * 10;

            this.teethGraphics.lineStyle(2 + Math.random() * 2, 0x110505, 0.6);
            this.teethGraphics.beginPath();
            this.teethGraphics.moveTo(baseX, baseY);
            this.teethGraphics.lineTo(baseX + drip, baseY + length);
            this.teethGraphics.strokePath();

            // Drip at end
            this.teethGraphics.fillStyle(0x110505, 0.7);
            this.teethGraphics.fillCircle(baseX + drip, baseY + length, 3);
        }
    }

    updateParticles(delta) {
        // Spawn consumption particles when player is close
        if (this.intensity > 0.3 && Math.random() < this.intensity * 0.3) {
            this.consumeParticles.push({
                x: this.x + this.width + 50 + Math.random() * 100,
                y: Math.random() * this.scene.scale.height,
                vx: -2 - Math.random() * 3,
                vy: (Math.random() - 0.5) * 2,
                life: 1,
                size: 2 + Math.random() * 4
            });
        }

        // Update particles
        this.bodyGraphics.lineStyle(0);

        this.consumeParticles = this.consumeParticles.filter(p => {
            p.x += p.vx;
            p.y += p.vy;
            p.life -= 0.02;

            // Accelerate toward mouth
            const mouthY = this.scene.scale.height / 2;
            p.vy += (mouthY - p.y) * 0.001;
            p.vx -= 0.1;

            if (p.life > 0 && p.x > this.x) {
                this.bodyGraphics.fillStyle(0x220011, p.life * 0.6);
                this.bodyGraphics.fillCircle(p.x, p.y, p.size * p.life);
                return true;
            }
            return false;
        });
    }

    // Get threat/intensity level
    getThreatLevel() {
        return this.intensity;
    }

    // Check if position is being "consumed"
    isConsuming(x) {
        return x <= this.x + this.width;
    }

    destroy() {
        if (this.sprite) this.sprite.destroy();
    }
}
